import { fromJS, is, List, Map } from 'immutable';

import { nestFlatQuery, flattenNestedObject, get } from 'ctm-util-functions';

const allowedActions = [
    'SET_VALUE',
    'SET_PAYLOAD_VALUE',
    'SET_VALUES',
    'SET_FORM_VALUES_FROM_STATE',
    'PRELOAD_VALUES',
    'RESET_VALUES',
    'RESET_VALUE_IF_SET',
    'RESET_FORM_STATE_VALUES',
    'SET_FORM_PAYLOAD'
];

/**
 * Reducer for setting, resetting and preloading values for form elements.
 * This closure allows us to reuse the reducer for multiple forms and pages as it allows us
 * to supply the initialState as an argument.
 * We can then use the returned, closured reducer in combineReducers.
 * @param  {Object} initialState  The initial version of the state that this reducer has access to
 * @param  {String} stateKey  The state key that this reducer should process actions for
 * @param  {Object} customCases  Any app specific reducer cases to be handled at the form reducer level.
 * @returns {Function} The reducer
 */
const createFormReducer = (initialState, stateKey, customCases = {}) => {
    return (state = initialState, action) => {
        if (allowedActions.includes(action.type) && !action.stateKey) {
            throw new Error(`Action sent to form reducer has no stateKey: ${JSON.stringify(action)}`);
        }
        if (action.stateKey !== stateKey) {
            return state;
        }
        switch (action.type) {
            case 'SET_VALUE': {
                /**
                 * Find action.identifier in state and set it's value to action.value.
                 */
                return state.setIn(action.identifier.split('.'), fromJS(action.value));
            }
            case 'SET_PAYLOAD_VALUE': {
                // encapsulating action.payload in fromJS() to convert the object into immutable map
                return state.setIn(action.identifier.split('.'), fromJS(action.payload));
            }
            case 'SET_VALUES': {
                /**
                 * Find action.identifier in state and set it's value to action.value.
                 */
                return state.mergeDeep(action.values);
            }
            case 'SET_FORM_VALUES_FROM_STATE': {
                if (!action.source) {
                    throw Error('SET_FORM_VALUES_FROM_STATE: The property action.source is required');
                }

                const sourceObj = state.getIn(action.source.split('.'));
                if (action.target) {
                    const target = {};
                    target[action.target] = sourceObj;
                    return state.mergeDeep(nestFlatQuery(target));
                }
                // If no target is set, merge to the top level of the form state tree
                return state.mergeDeep(sourceObj);
            }
            case 'SET_VALUE_IN_ARRAY': {
                // don't update if no value defined
                if (!action.value) {
                    return state;
                }

                const keyPath = action.identifier.split('.');
                const targetArray = state.getIn(keyPath);
                // use fromJS here to convert value to immutable. If value is primitive value, e.g. string, boolean, then it won't do anything
                const targetValue = fromJS(action.value);

                // Check if the target state value at keyPath is List(), only add/remove if it is a list
                if (List.isList(targetArray)) {
                    // if it is a duplication, remove it
                    // @todo: currently it doesn't allow duplication in list, check future use cases
                    if (targetArray.includes(targetValue)) {
                        return state.updateIn(keyPath, (list) => list.filterNot((value) => is(value, targetValue)).toSet().toList());
                    }

                    // if itemKey is defined and targetValue is an object, we check if the target array has the item that has the same itemKey value, then update it
                    if (action.itemKey && Map.isMap(targetValue)) {
                        return state.updateIn(keyPath, (list) => {
                            const index = list.findIndex((item) => item.get(action.itemKey) === targetValue.get(action.itemKey));
                            // if index < 0, it means we couldn't find the matching itemKey value, don't update the list
                            if (index < 0) return list;
                            // otherwise, found it, update to the targetValue
                            return list.set(index, targetValue);
                        });
                    }
                    // otherwise add it to the list
                    // @todo currently use List.unshift so new value is at the top of the list, may need to expand the use case to give more options so we can push to the last
                    return state.updateIn(keyPath, (list) => list.unshift(targetValue).toSet().toList());
                }

                // if target state is not array, e.g. null, we create a new list, then add to it
                return state.setIn(keyPath, List([targetValue]));
            }
            case 'PRELOAD_VALUES': {
                /**
                 * Create and return a new state object with the identifiers and
                 * values in action.
                 */
                return state.mergeDeep(action.preloadSchema);
            }
            case 'RESET_VALUES': {
                /**
                 * Copy the state, and set the values for each id in
                 * action.identifiers to null.
                 */
                if (!action.identifiers && !action.baseIdentifier) {
                    throw Error('RESET_VALUES: Identifiers or baseIdentifier must be provided');
                }

                let newState = state;
                for (const identifier of action.identifiers) {
                    const subState = state.getIn(identifier.split('.'));
                    if (Map.isMap(subState)) {
                        const flatSubState = flattenNestedObject(subState.toJS(), identifier, null);
                        const nulledSubstate = Object.keys(flatSubState).reduce((acc, val) => {
                            acc[val] = null;
                            return acc;
                        }, {});
                        newState = newState.mergeDeep(nestFlatQuery(nulledSubstate));
                    } else if (subState === undefined) {
                        continue;
                    } else {
                        newState = newState.mergeDeep(nestFlatQuery({ [identifier]: null }));
                    }
                }
                return newState;
            }
            case 'RESET_VALUE_IF_SET': {
                const path = action.identifier.split('.');
                const currentStateValue = state.getIn(path);
                if (currentStateValue === action.value) {
                    return state.setIn(path, null);
                }
                return state;
            }
            case 'RESET_FORM_STATE_VALUES_IF_SET': {
                let sourceObj = state.getIn(action.identifier.split('.'));
                const defaultValue = action.defaultValue || null;
                const value = action.value;
                for (const key of sourceObj.keys()) {
                    if (sourceObj.get(key) === value) {
                        sourceObj = sourceObj.set(key, defaultValue);
                    }
                }
                return state.setIn(action.identifier.split('.'), sourceObj);
            }
            case 'RESET_FORM_STATE_VALUES': {
                let sourceObj = state.getIn(action.identifier.split('.'));
                for (const key of sourceObj.keys()) {
                    if (get(action, 'exceptions', []).includes(key)) {
                        continue;
                    }
                    sourceObj = sourceObj.set(key, null);
                }
                return state.setIn(action.identifier.split('.'), sourceObj);
            }
            case 'SET_FORM_PAYLOAD': {
                const formPayload = action.payload.form;
                const flatFormPayload = flattenNestedObject(formPayload);
                // replace true/false to Y/N in the flat payload
                // eslint-disable-next-line no-return-assign
                Object.keys(flatFormPayload).forEach((key) => ((flatFormPayload[key] === true) && (flatFormPayload[key] = 'Y'))
                    || ((flatFormPayload[key] === false) && (flatFormPayload[key] = 'N'))
                );

                return state.mergeDeep(nestFlatQuery(flatFormPayload));
            }
            default: {
                // Vertical specific custom cases
                for (const customCase of Object.keys(customCases)) {
                    if (action.type === customCase) {
                        return customCases[customCase](state, action);
                    }
                }
                // Supplied action not handled, return the state.
                // We need this to handle the @@redux/init actions that combineReducers
                // fires to check the reducer for bad patterns, this won't be hit
                // during normal operation.
                return state;
            }
        }
    };
};

export { createFormReducer };
